import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const divisions = [
    { name: 'ALM Trading LLC', path: '/services/trading' },
    { name: 'ALM Consultants LLC', path: '/services/consultancy' },
    { name: 'ALM IT Services', path: '/services/it-services' },
    { name: 'ALM Software Services', path: '/services/software' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-card/95 backdrop-blur-lg shadow-lg border-b border-border' : 'bg-card/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img 
              src="/logo-header.png" 
              alt="ALM Group of Companies" 
              className="h-16 w-auto transition-all duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link to="/">
              <Button
                variant="ghost"
                className={`text-foreground hover:text-primary hover:bg-secondary transition-colors ${
                  location.pathname === '/' ? 'text-primary bg-secondary' : ''
                }`}
              >
                Home
              </Button>
            </Link>
            <Link to="/about">
              <Button
                variant="ghost"
                className={`text-foreground hover:text-primary hover:bg-secondary transition-colors ${
                  location.pathname === '/about' ? 'text-primary bg-secondary' : ''
                }`}
              >
                About Us
              </Button>
            </Link>

            {/* Services Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-foreground hover:text-primary hover:bg-secondary">
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 bg-card border border-border">
                      {divisions.map((division) => (
                        <li key={division.path}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={division.path}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-secondary hover:text-primary focus:bg-secondary"
                            >
                              <div className="text-sm font-medium text-foreground">{division.name}</div>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link to="/contact">
              <Button
                variant="ghost"
                className={`text-foreground hover:text-primary hover:bg-secondary transition-colors ${
                  location.pathname === '/contact' ? 'text-primary bg-secondary' : ''
                }`}
              >
                Contact
              </Button>
            </Link>
            <Link to="/payment">
              <Button className="ml-4 bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/50 hover:shadow-primary/80 transition-all duration-300">
                Make a Payment
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-foreground hover:text-primary hover:bg-secondary transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden bg-card border-t border-border shadow-lg">
          <div className="px-4 pt-2 pb-4 space-y-2">
            <Link to="/" onClick={() => setIsOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary hover:bg-secondary">
                Home
              </Button>
            </Link>
            <Link to="/about" onClick={() => setIsOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary hover:bg-secondary">
                About Us
              </Button>
            </Link>
            <div className="space-y-1">
              <div className="px-4 py-2 text-sm font-semibold text-foreground">Services</div>
              {divisions.map((division) => (
                <Link key={division.path} to={division.path} onClick={() => setIsOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start pl-8 text-foreground hover:text-primary hover:bg-secondary">
                    {division.name}
                  </Button>
                </Link>
              ))}
            </div>
            <Link to="/contact" onClick={() => setIsOpen(false)}>
              <Button variant="ghost" className="w-full justify-start text-foreground hover:text-primary hover:bg-secondary">
                Contact
              </Button>
            </Link>
            <Link to="/payment" onClick={() => setIsOpen(false)}>
              <Button className="w-full bg-primary hover:bg-primary/90 text-white mt-4 shadow-lg shadow-primary/50">
                Make a Payment
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
